import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Image } from "@/components/ui/image";
import { formatMXN } from "@/lib/format";
import { useStoreCart, setQty, removeFromCart, clearCart, cartSubtotal, cartCount } from "@/lib/storeCart";
import { ShoppingCart, Trash2, Zap, X, Plus, Minus } from "lucide-react";

// Sidebar deslizable desde la izquierda: acceso rapido al carrito con control
// de cantidades sin salir de la pagina actual. Se abre desde el boton flotante.
export default function CartDrawer({ open, onClose }) {
  const items = useStoreCart();
  const nav = useNavigate();
  const subtotal = cartSubtotal(items);
  const count = cartCount(items);

  // Bloquea scroll de fondo y cierra con Escape.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-hidden="true"
      />
      {/* Panel lateral */}
      <aside
        role="dialog"
        aria-label="Carrito de compras"
        className={`fixed left-0 top-0 z-50 h-full w-full max-w-md bg-ink border-r border-ink-border shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Cabecera */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-ink-border safe-top">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-cyan" />
            <h2 className="font-display font-bold text-sm uppercase tracking-wide">Tu carrito</h2>
            {count > 0 && <span className="bg-cyan/15 text-cyan text-xs px-1.5 py-0.5 font-bold border border-cyan/40">{count}</span>}
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="touch-icon p-2 text-ink-muted hover:text-ink-text">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cuerpo */}
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <ShoppingCart className="w-12 h-12 mb-3 text-ink-muted opacity-30" />
            <p className="text-sm text-ink-muted mb-4">Tu carrito está vacío.</p>
            <Link to="/tienda" onClick={onClose} className="inline-flex items-center gap-1.5 text-cyan text-sm font-medium">
              Explorar la tienda
            </Link>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {items.map((it) => (
              <div key={it.id} className="surface p-2.5 flex gap-3 items-center">
                <Link to={`/tienda/${it.slug}`} onClick={onClose} className="w-14 h-14 shrink-0 bg-white border border-ink-border overflow-hidden">
                  {it.image ? (
                    <Image src={it.image} alt={it.title} fittingType="fit" className="w-full h-full" />
                  ) : (
                    <div className="w-full h-full" />
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/tienda/${it.slug}`} onClick={onClose} className="text-sm font-medium hover:text-cyan line-clamp-1">{it.title}</Link>
                  <p className="text-xs text-ink-muted">{formatMXN(it.price)} c/u</p>
                  <div className="font-display font-bold text-cyan text-sm mt-0.5">{formatMXN(it.price * it.qty)}</div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <div className="flex items-center border border-ink-border">
                    <button onClick={() => setQty(it.id, it.qty - 1)} aria-label="Restar" className="touch-icon px-2 py-1.5 text-ink-muted hover:text-cyan">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-2.5 py-1.5 text-sm text-center min-w-8">{it.qty}</span>
                    <button onClick={() => setQty(it.id, it.qty + 1)} aria-label="Sumar" className="touch-icon px-2 py-1.5 text-ink-muted hover:text-cyan">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button onClick={() => removeFromCart(it.id)} aria-label="Quitar" className="touch-icon text-ink-muted hover:text-destructive p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            <button onClick={clearCart} className="text-xs text-ink-muted hover:text-destructive mt-1 inline-flex items-center gap-1 px-1">
              <Trash2 className="w-3.5 h-3.5" /> Vaciar carrito
            </button>
          </div>
        )}

        {/* Pie con totales y CTA */}
        {items.length > 0 && (
          <div className="border-t border-ink-border px-4 py-3.5 safe-bottom space-y-2.5 bg-ink-surface">
            <div className="flex justify-between text-sm">
              <span className="text-ink-muted">Subtotal</span>
              <span className="font-display font-bold">{formatMXN(subtotal)}</span>
            </div>
            <p className="text-xs text-ink-muted">Envío se calcula al pagar.</p>
            <button
              onClick={() => { onClose(); nav("/pagar"); }}
              className="w-full inline-flex items-center justify-center gap-2 bg-cyan hover:bg-cyan-hover text-ink px-4 py-3 font-display font-bold uppercase tracking-wide glow-cyan transition-colors"
            >
              <Zap className="w-4 h-4" /> Ir a pagar
            </button>
            <Link to="/carrito" onClick={onClose} className="block text-center text-xs text-ink-muted hover:text-cyan">
              Ver carrito completo
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}